import React from 'react';
import clsx from 'clsx';
import {
  notEmptyString,
  pluralize,
} from '@populateui/utils';
import {
  AppointmentPatientAlertDTO,
} from 'dtos';
import {
  AlertSeverity,
} from 'dtos/alertSeverity';
import ToolTip from 'core/components/Tooltip';

type Props = {
  alerts?: AppointmentPatientAlertDTO[] | null;
};

type AlertSeverityPresentation = {
  className: string;
  priority: number;
};

type AppointmentAlertsTooltipProps = {
  alerts: AppointmentPatientAlertDTO[];
};

const ALERT_SEVERITY_PRESENTATION: Record<AlertSeverity, AlertSeverityPresentation> = {
  [AlertSeverity.Error]: {
    className: 'bg-red-500',
    priority: 4,
  },
  [AlertSeverity.Warning]: {
    className: 'bg-amber-500',
    priority: 3,
  },
  [AlertSeverity.Info]: {
    className: 'bg-blue-500',
    priority: 2,
  },
  [AlertSeverity.Neutral]: {
    className: 'bg-gray-500',
    priority: 1,
  },
};

const foldClipPath = {
  clipPath: 'polygon(100% 0, 0 0, 100% 100%)',
};

function getHighestAlertSeverity(
  alerts: AppointmentPatientAlertDTO[],
): AlertSeverity {
  return alerts.reduce<AlertSeverity>((highestSeverity, alert) => {
    const severity = alert.severity ?? AlertSeverity.Neutral;
    const severityPriority = ALERT_SEVERITY_PRESENTATION[severity].priority;
    const highestPriority = ALERT_SEVERITY_PRESENTATION[highestSeverity].priority;

    return severityPriority > highestPriority ? severity : highestSeverity;
  }, AlertSeverity.Neutral);
}

function AppointmentAlertsTooltip({
  alerts,
}: AppointmentAlertsTooltipProps) {
  return (
    <div className="flex max-h-64 max-w-80 flex-col overflow-y-auto">
      {alerts.map((alert, alertIndex) => {
        const severity = alert.severity ?? AlertSeverity.Neutral;
        const presentation = ALERT_SEVERITY_PRESENTATION[severity];

        return (
          <div
            key={alert.alertId ?? `${alert.type ?? 'alert'}-${alertIndex}`}
            className="flex items-start gap-1.5 border-b border-gray-200 py-1.5 last:border-b-0"
          >
            <span
              aria-hidden="true"
              className={clsx(
                'mt-1.5 h-2 w-2 shrink-0 rounded-full',
                presentation.className,
              )}
            />
            <div className="flex min-w-0 flex-1 flex-col">
              <span className="font-semibold text-gray-900">
                {notEmptyString(alert.label) ? alert.label : 'Patient alert'}
              </span>
              {notEmptyString(alert.text) && (
                <span className="text-gray-600">{alert.text}</span>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

export default function AppointmentAlertIndicator({
  alerts,
}: Props) {
  const normalizedAlerts = alerts ?? [];
  const hasAlerts = normalizedAlerts.length > 0;
  const highestSeverity = getHighestAlertSeverity(normalizedAlerts);
  const presentation = ALERT_SEVERITY_PRESENTATION[highestSeverity];
  const alertLabel = `${normalizedAlerts.length} patient ${pluralize('alert', normalizedAlerts.length, false)}`;

  return hasAlerts ? (
    <div className="absolute right-0 top-0 z-10">
      <ToolTip
        className="!w-auto"
        title={<AppointmentAlertsTooltip alerts={normalizedAlerts} />}
        isInteractive
      >
        <span
          aria-label={alertLabel}
          className="relative block h-3 w-3"
        >
          <span
            aria-hidden="true"
            className="absolute inset-0 bg-white"
            style={foldClipPath}
          />
          <span
            aria-hidden="true"
            className={clsx(
              'absolute right-0 top-0 h-2.5 w-2.5',
              presentation.className,
            )}
            style={foldClipPath}
          />
        </span>
      </ToolTip>
    </div>
  ) : null;
}
